/**
 * Bone overlay — screen-space-scaled joints with click-to-select.
 *
 * Shared between MannequinRenderer (.glb bones) and SceneRenderer
 * (.blend armature bones). Renders interactive joint spheres at bone
 * world positions, bone stick lines, lock indicators, and debug info.
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import { getRestPose } from "../mannequin/boneRegistry.ts";
import { getConstraint } from "../mannequin/constraints.ts";
import {
  decomposeSwingTwist,
  extractAxisAngle,
} from "../mannequin/quaternionMath.ts";
import { createRafCoalescer } from "../rafCoalescer.ts";
import { useDispose, useDisposeMany } from "./hooks/useDispose.ts";
import { POINT_HIT_PX, POINT_VISUAL_PX } from "./overlayConstants.ts";
import { makeScratch } from "./scratch.ts";
import { screenToWorldRadius } from "./screenRadius.ts";

const BONE_COLOR = 0xe8a030;
const BONE_COLOR_SELECTED = 0x40d8ff;
const BONE_COLOR_VIOLATION = 0xee8888;
const BONE_COLOR_LIMIT_RED = 0xff3333;
const BONE_COLOR_REST = 0x44cc66;
const BONE_COLOR_OUTLINE = 0x1a1a1a;

const boneScratch = makeScratch({ color: "color" });
const _limitRed = new THREE.Color(BONE_COLOR_LIMIT_RED);
const _limitBase = new THREE.Color(BONE_COLOR);

/** Threshold for considering a bone "at rest" (dot product of quaternions). */
const REST_DOT_THRESHOLD = 0.9999;

function isQuaternionAtRest(
  q: THREE.Quaternion,
  rest: [number, number, number, number],
): boolean {
  const dot = Math.abs(
    q.x * rest[0] + q.y * rest[1] + q.z * rest[2] + q.w * rest[3],
  );
  return dot >= REST_DOT_THRESHOLD;
}

const unitSphereGeo = new THREE.SphereGeometry(1, 10, 8);
const unitOctahedronGeo = new THREE.OctahedronGeometry(1);
const unitBoxGeo = new THREE.BoxGeometry(1, 1, 1);
/** Outline for box joints — clean wireframe edges. */
const outlineBoxEdgesGeo = new THREE.EdgesGeometry(
  new THREE.BoxGeometry(1, 1, 1),
  30,
);
const hitMat = new THREE.MeshBasicMaterial({ visible: false });

// ---------------------------------------------------------------------------
// Grab handle — a virtual joint behind the root bone for body-grab (IK with
// pinned extremities). Rendered and interactive exactly like a normal bone
// joint, but positioned at a fixed object-space offset behind the mannequin
// (root's local -Z direction). Offset is proportional to the skeleton's size.
// ---------------------------------------------------------------------------

const GRAB_HANDLE_NAME = "__grabHandle__";
/** Grab handle offset as a multiple of the root→head distance. */
const GRAB_HANDLE_OFFSET_FACTOR = 0.2;
const gh = makeScratch({ quat: "quat", dir: "vec3" });

// ---------------------------------------------------------------------------
// Constraint-violation glow — a radial gradient sprite that appears under the
// gizmo/joint when constraints prevent following the user's input.
// ---------------------------------------------------------------------------

function createViolationGlowTexture(): THREE.Texture {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const gradient = ctx.createRadialGradient(
    size / 2,
    size / 2,
    0,
    size / 2,
    size / 2,
    size / 2,
  );
  gradient.addColorStop(0, "rgba(238, 136, 136, 0.55)");
  gradient.addColorStop(0.4, "rgba(238, 136, 136, 0.25)");
  gradient.addColorStop(1, "rgba(238, 136, 136, 0)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

const violationGlowTexture = createViolationGlowTexture();

export function BoneOverlay({
  allBones,
  majorBones,
  objectId,
  debugEnabled,
}: {
  allBones: THREE.Bone[];
  majorBones: THREE.Bone[];
  objectId: string;
  debugEnabled: boolean;
}) {
  const selectedBones = useStore((s) =>
    s.poseMode?.objectId === objectId ? s.poseMode?.selectedBones : null,
  );
  const activeBone = useStore((s) =>
    s.poseMode?.objectId === objectId ? s.poseMode?.activeBone : null,
  );
  const ikDragState = useStore((s) => s.ikDragState);
  const lockedBones = useStore((s) => s.lockedBones.get(objectId));
  const pinnedBones = useStore((s) => s.pinnedBones.get(objectId));
  const selectBone = useStore((s) => s.selectBone);
  const { camera, size: viewportSize } = useThree();

  // Line geometry for bone sticks (all bones, including fingers) + 1 for
  // the root→grab-handle connector.
  const { lineGeo, lineMat } = useMemo(() => {
    let segCount = 1; // +1 for grab handle connector
    for (const bone of allBones) {
      for (const child of bone.children) {
        if (child instanceof THREE.Bone) segCount++;
      }
    }
    const lg = new THREE.BufferGeometry();
    lg.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(new Float32Array(segCount * 6), 3),
    );
    const lm = new THREE.LineBasicMaterial({
      color: BONE_COLOR,
      depthTest: false,
      depthWrite: false,
    });
    return { lineGeo: lg, lineMat: lm };
  }, [allBones]);
  useDisposeMany(lineGeo, lineMat);

  // Per-joint refs for position + scale updates
  const jointRefs = useRef<Map<string, THREE.Group>>(new Map());

  // Per-bone visual mesh refs and limit-warning materials
  const meshRefs = useRef<Map<string, THREE.Mesh>>(new Map());
  const limitMats = useRef<Map<string, THREE.MeshBasicMaterial>>(new Map());

  // Per-bone limit-warning materials are cloned lazily in useFrame; nothing
  // else owns them, so the component must dispose them on unmount.
  useEffect(() => {
    const mats = limitMats.current;
    return () => {
      for (const m of mats.values()) m.dispose();
      mats.clear();
    };
  }, []);

  // Scratch
  const tmpVec = useMemo(() => new THREE.Vector3(), []);
  const tmpVec2 = useMemo(() => new THREE.Vector3(), []);

  // Per-frame: update stick lines, position + scale joint spheres
  useFrame(() => {
    const cam = camera as THREE.PerspectiveCamera;
    const posAttr = lineGeo.getAttribute("position") as THREE.BufferAttribute;
    const posArr = posAttr.array as Float32Array;

    let segIdx = 0;
    for (const bone of allBones) {
      bone.getWorldPosition(tmpVec);

      for (const child of bone.children) {
        if (child instanceof THREE.Bone) {
          child.getWorldPosition(tmpVec2);
          const off = segIdx * 6;
          posArr[off] = tmpVec.x;
          posArr[off + 1] = tmpVec.y;
          posArr[off + 2] = tmpVec.z;
          posArr[off + 3] = tmpVec2.x;
          posArr[off + 4] = tmpVec2.y;
          posArr[off + 5] = tmpVec2.z;
          segIdx++;
        }
      }
    }
    // Root → grab handle bone stick
    const ghRef = jointRefs.current.get(GRAB_HANDLE_NAME);
    if (ghRef) {
      const off = segIdx * 6;
      const rootBone = allBones.find((b) => b.name === "root");
      if (rootBone) {
        rootBone.getWorldPosition(tmpVec);
        posArr[off] = tmpVec.x;
        posArr[off + 1] = tmpVec.y;
        posArr[off + 2] = tmpVec.z;
      }
      posArr[off + 3] = ghRef.position.x;
      posArr[off + 4] = ghRef.position.y;
      posArr[off + 5] = ghRef.position.z;
      segIdx++;
    }
    posAttr.needsUpdate = true;

    for (const bone of majorBones) {
      const g = jointRefs.current.get(bone.name);
      if (!g) continue;
      bone.getWorldPosition(tmpVec);
      g.position.copy(tmpVec);

      g.scale.setScalar(
        screenToWorldRadius(1, tmpVec, cam, viewportSize.height),
      );
    }

    // Position the grab handle joint — same pattern as bone joints above,
    // but at a fixed object-space offset behind the root bone (root's local
    // -Z direction). The offset distance is proportional to the skeleton's
    // height (root→head) so it scales correctly with any mannequin size.
    const ghGroup = jointRefs.current.get(GRAB_HANDLE_NAME);
    if (ghGroup) {
      const rootBone = allBones.find((b) => b.name === "root");
      const headBone = allBones.find((b) => b.name === "head");
      if (rootBone && headBone) {
        rootBone.getWorldPosition(tmpVec);
        headBone.getWorldPosition(tmpVec2);
        const skeletonHeight = tmpVec.distanceTo(tmpVec2);
        const offset = skeletonHeight * GRAB_HANDLE_OFFSET_FACTOR;

        rootBone.getWorldQuaternion(gh.quat);
        // Backward: root's local -Z in world space
        gh.dir.set(0, 0, -1).applyQuaternion(gh.quat);
        ghGroup.position.copy(tmpVec).addScaledVector(gh.dir, offset);

        // Screen-space scale for the visual (same as all other joints)
        ghGroup.scale.setScalar(
          screenToWorldRadius(1, ghGroup.position, cam, viewportSize.height),
        );
      }
    }

    // Limit-proximity coloring: modulate unselected bone spheres toward red
    const poseState = useStore.getState().poseMode;
    const selBones =
      poseState?.objectId === objectId ? poseState?.selectedBones : null;
    const lkBones = useStore.getState().lockedBones.get(objectId);
    const pnBones = useStore.getState().pinnedBones.get(objectId);
    for (const bone of majorBones) {
      const mesh = meshRefs.current.get(bone.name);
      if (!mesh) continue;
      const isSel = selBones?.has(bone.name) ?? false;
      const isLk = lkBones?.has(bone.name) ?? false;
      const isPn = pnBones?.has(bone.name) ?? false;
      if (isSel || isLk || isPn) {
        // Reset to default material if it was overridden
        if (
          mesh.material !== materials.normal &&
          mesh.material !== materials.selected &&
          mesh.material !== materials.locked &&
          mesh.material !== materials.pinned &&
          mesh.material !== materials.violation
        ) {
          // Leave material as-is; JSX will set it next render
        }
        continue;
      }
      // Check if bone is at rest pose (green) or near limit (red blend)
      const restPose = getRestPose(objectId, bone.name);
      const isAtRest = restPose
        ? isQuaternionAtRest(bone.quaternion, restPose)
        : false;
      if (isAtRest) {
        mesh.material = materials.rest;
      } else {
        const proximity = computeLimitProximity(bone, objectId);
        if (proximity > 0.01) {
          let mat = limitMats.current.get(bone.name);
          if (!mat) {
            mat = materials.normal.clone();
            limitMats.current.set(bone.name, mat);
          }
          boneScratch.color.copy(_limitBase).lerp(_limitRed, proximity);
          mat.color.copy(boneScratch.color);
          mesh.material = mat;
        } else {
          mesh.material = materials.normal;
        }
      }
    }

    // Billboard locked × indicators to face camera
    for (const [, xGroup] of lockXRefs.current) {
      xGroup.quaternion.copy(cam.quaternion);
    }

    // Billboard pinned ◆ indicators to face camera
    for (const [, dGroup] of pinDiamondRefs.current) {
      dGroup.quaternion.copy(cam.quaternion);
    }

    // Position and scale the constraint-violation glow sprite
    const glow = violationGlowRef.current;
    if (glow) {
      const ds = useStore.getState().ikDragState;
      if (ds?.isViolating) {
        glow.visible = true;
        glow.position.set(
          ds.violationWorldPos[0],
          ds.violationWorldPos[1],
          ds.violationWorldPos[2],
        );
        const glowScale = screenToWorldRadius(
          60,
          glow.position,
          cam,
          viewportSize.height,
        );
        glow.scale.set(glowScale, glowScale, 1);
      } else {
        glow.visible = false;
      }
    }
  });

  // Tap vs drag detection for bone interaction.
  // Tap: select the bone (shows gizmo for current transform mode).
  // Drag: select + immediately begin free-axis boneTranslate.
  const pointerDownPos = useRef<{ x: number; y: number; bone: string } | null>(
    null,
  );
  const dragActiveRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const TAP_THRESHOLD = 8;

  const handleJointPointerDown = useCallback(
    (boneName: string) => (e: any) => {
      e.stopPropagation();
      // Don't start if a command is already active
      if (useStore.getState().commandBuffer) return;

      const ne = e.nativeEvent as PointerEvent;
      const isGH = boneName === GRAB_HANDLE_NAME;
      pointerDownPos.current = { x: ne.clientX, y: ne.clientY, bone: boneName };
      dragActiveRef.current = false;
      const livePreview = createRafCoalescer<{ clientX: number; clientY: number }>(
        ({ clientX, clientY }) => {
          useStore.getState().applyCommandLive(clientX, clientY);
        },
      );

      const onMove = (ev: PointerEvent) => {
        const down = pointerDownPos.current;
        if (!down || dragActiveRef.current) {
          if (dragActiveRef.current) {
            livePreview.schedule({ clientX: ev.clientX, clientY: ev.clientY });
          }
          return;
        }
        const dx = ev.clientX - down.x;
        const dy = ev.clientY - down.y;
        if (dx * dx + dy * dy > TAP_THRESHOLD * TAP_THRESHOLD) {
          // Threshold exceeded — select bone and start free translate
          dragActiveRef.current = true;
          if (isGH) {
            selectBone("hips", objectId);
            useStore.getState().enterCommand("boneTranslate", {
              axis: null,
              gizmoInitiated: true,
              bodyGrab: true,
            });
          } else {
            selectBone(down.bone, objectId);
            useStore.getState().enterCommand("boneTranslate", {
              axis: null,
              gizmoInitiated: true,
            });
          }
          livePreview.schedule({ clientX: ev.clientX, clientY: ev.clientY });
          livePreview.flush();
        }
      };

      const onUp = (ev: PointerEvent) => {
        const down = pointerDownPos.current;
        pointerDownPos.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        cleanupRef.current = null;

        if (dragActiveRef.current) {
          // Finish the drag command
          livePreview.flush();
          dragActiveRef.current = false;
          useStore.getState().confirmCommand();
        } else if (down) {
          // Tap — select hips for grab handle, normal select for bones
          const tapBone = isGH ? "hips" : boneName;
          if (ev.shiftKey) {
            selectBone(tapBone, objectId, true);
          } else {
            selectBone(
              selectedBones?.has(tapBone) && selectedBones.size === 1
                ? null
                : tapBone,
              objectId,
            );
          }
        }
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      cleanupRef.current = () => {
        livePreview.cancel();
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };
    },
    [selectBone, selectedBones, objectId],
  );

  // Clean up window listeners on unmount
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  // Cancel drag if command is cancelled externally (e.g. Escape)
  const commandBuffer = useStore((s) => s.commandBuffer);
  useEffect(() => {
    if (!commandBuffer && dragActiveRef.current) {
      dragActiveRef.current = false;
      cleanupRef.current?.();
      cleanupRef.current = null;
      pointerDownPos.current = null;
    }
  }, [commandBuffer]);

  // Materials (created once)
  const materials = useMemo(
    () => ({
      normal: new THREE.MeshBasicMaterial({
        color: BONE_COLOR,
        depthTest: false,
        depthWrite: false,
      }),
      selected: new THREE.MeshBasicMaterial({
        color: BONE_COLOR_SELECTED,
        depthTest: false,
        depthWrite: false,
      }),
      locked: new THREE.MeshBasicMaterial({
        color: 0x888888,
        depthTest: false,
        depthWrite: false,
      }),
      pinned: new THREE.MeshBasicMaterial({
        color: 0x44bb44,
        depthTest: false,
        depthWrite: false,
      }),
      violation: new THREE.MeshBasicMaterial({
        color: BONE_COLOR_VIOLATION,
        depthTest: false,
        depthWrite: false,
      }),
      rest: new THREE.MeshBasicMaterial({
        color: BONE_COLOR_REST,
        depthTest: false,
        depthWrite: false,
      }),
      outline: new THREE.MeshBasicMaterial({
        color: BONE_COLOR_OUTLINE,
        depthTest: false,
        depthWrite: false,
      }),
    }),
    [],
  );
  useDisposeMany(
    materials.normal,
    materials.selected,
    materials.locked,
    materials.pinned,
    materials.violation,
    materials.rest,
    materials.outline,
  );

  // Violation glow sprite material (created once)
  const violationGlowMat = useMemo(
    () =>
      new THREE.SpriteMaterial({
        map: violationGlowTexture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    [],
  );
  useDispose(violationGlowMat);

  // Ref for the violation glow sprite (positioned per-frame)
  const violationGlowRef = useRef<THREE.Sprite>(null);

  // × indicator geometry for locked joints
  const lockXGeo = useMemo(() => {
    const s = POINT_VISUAL_PX * 0.6;
    const verts = new Float32Array([-s, -s, 0, s, s, 0, s, -s, 0, -s, s, 0]);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
    return geo;
  }, []);

  const lockXMat = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: 0xffffff,
        depthTest: false,
        linewidth: 2,
      }),
    [],
  );
  useDisposeMany(lockXGeo, lockXMat);
  const lockXRefs = useRef<Map<string, THREE.Group>>(new Map());

  // ◆ diamond indicator geometry for pinned joints
  const pinDiamondGeo = useMemo(() => {
    const s = POINT_VISUAL_PX * 0.55;
    const verts = new Float32Array([
      0,
      s,
      0,
      s,
      0,
      0, // top → right
      s,
      0,
      0,
      0,
      -s,
      0, // right → bottom
      0,
      -s,
      0,
      -s,
      0,
      0, // bottom → left
      -s,
      0,
      0,
      0,
      s,
      0, // left → top
    ]);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
    return geo;
  }, []);

  const pinDiamondMat = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: 0x44bb44,
        depthTest: false,
        linewidth: 2,
      }),
    [],
  );
  useDisposeMany(pinDiamondGeo, pinDiamondMat);
  const pinDiamondRefs = useRef<Map<string, THREE.Group>>(new Map());

  // Outline material for all joints
  const outlineMat = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: BONE_COLOR_OUTLINE,
        depthTest: false,
        depthWrite: false,
      }),
    [],
  );
  useDispose(outlineMat);

  return (
    <group userData={{ excludeFromExport: true }}>
      <lineSegments geometry={lineGeo} material={lineMat} renderOrder={999} />

      {debugEnabled &&
        activeBone &&
        (() => {
          const bone = allBones.find((b) => b.name === activeBone);
          return bone ? (
            <BoneDebugDataPump bone={bone} objectId={objectId} />
          ) : null;
        })()}

      {/* Constraint-violation glow sprite — positioned per-frame in useFrame */}
      <sprite
        ref={violationGlowRef}
        material={violationGlowMat}
        visible={false}
        renderOrder={998}
      />

      {majorBones.map((bone) => {
        const isSelected = selectedBones?.has(bone.name) ?? false;
        const isLocked = lockedBones?.has(bone.name) ?? false;
        const isPinned = pinnedBones?.has(bone.name) ?? false;
        const isActive = bone.name === activeBone;
        const isViolating = isActive && (ikDragState?.isViolating ?? false);
        const isRoot = bone.name === "root";
        const constraint = getConstraint(bone.name);
        const isHinge = constraint?.type === "hinge";
        const geo = isRoot
          ? unitBoxGeo
          : isHinge
            ? unitOctahedronGeo
            : unitSphereGeo;
        const r = isRoot ? POINT_VISUAL_PX * 0.75 : POINT_VISUAL_PX / 2;
        const mat = isViolating
          ? materials.violation
          : isLocked
            ? materials.locked
            : isPinned && !isSelected
              ? materials.pinned
              : isSelected
                ? materials.selected
                : materials.normal;
        return (
          <group
            key={bone.name}
            ref={(g) => {
              if (g) jointRefs.current.set(bone.name, g);
              else jointRefs.current.delete(bone.name);
            }}
          >
            {/* Outline — solid backing mesh for round joints, wireframe for box */}
            {isRoot ? (
              <lineSegments
                geometry={outlineBoxEdgesGeo}
                material={outlineMat}
                scale={[r + 1, r + 1, r + 1]}
                renderOrder={1001}
              />
            ) : (
              <mesh
                geometry={geo}
                material={materials.outline}
                scale={[r + 1.5, r + 1.5, r + 1.5]}
                renderOrder={999}
              />
            )}
            <mesh
              ref={(m) => {
                if (m) meshRefs.current.set(bone.name, m);
                else meshRefs.current.delete(bone.name);
              }}
              geometry={geo}
              material={mat}
              scale={[r, r, r]}
              renderOrder={1000}
            />
            {isLocked && isSelected && (
              <mesh
                geometry={geo}
                material={materials.selected}
                scale={[r + 1, r + 1, r + 1]}
                renderOrder={999}
              />
            )}
            {isLocked && (
              <group
                ref={(g) => {
                  if (g) lockXRefs.current.set(bone.name, g);
                  else lockXRefs.current.delete(bone.name);
                }}
              >
                <lineSegments
                  geometry={lockXGeo}
                  material={lockXMat}
                  renderOrder={1002}
                />
              </group>
            )}
            {isPinned && (
              <group
                ref={(g) => {
                  if (g) pinDiamondRefs.current.set(bone.name, g);
                  else pinDiamondRefs.current.delete(bone.name);
                }}
              >
                <lineSegments
                  geometry={pinDiamondGeo}
                  material={pinDiamondMat}
                  renderOrder={1002}
                />
              </group>
            )}
            <mesh
              geometry={geo}
              material={hitMat}
              scale={[POINT_HIT_PX / 2, POINT_HIT_PX / 2, POINT_HIT_PX / 2]}
              renderOrder={1001}
              onPointerDown={handleJointPointerDown(bone.name)}
            />
          </group>
        );
      })}

      {/* Grab handle — positioned per-frame at a world-space offset behind
          the root bone. Rendered and interactive exactly like a bone joint. */}
      <group
        ref={(g) => {
          if (g) jointRefs.current.set(GRAB_HANDLE_NAME, g);
          else jointRefs.current.delete(GRAB_HANDLE_NAME);
        }}
      >
        <mesh
          geometry={unitBoxGeo}
          material={materials.normal}
          scale={[
            POINT_VISUAL_PX * 0.75,
            POINT_VISUAL_PX * 0.75,
            POINT_VISUAL_PX * 0.75,
          ]}
          renderOrder={1000}
        />
        <mesh
          geometry={unitBoxGeo}
          material={hitMat}
          scale={[POINT_HIT_PX / 2, POINT_HIT_PX / 2, POINT_HIT_PX / 2]}
          renderOrder={1001}
          onPointerDown={handleJointPointerDown(GRAB_HANDLE_NAME)}
        />
      </group>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Limit-proximity computation — determines how close a bone is to its
// rotation constraint limits. Returns 0 (not near) to 1 (at limit).
// Only activates when >30% from rest AND within 15% of a limit boundary.
// ---------------------------------------------------------------------------

const lp = makeScratch({
  restQ: "quat",
  deltaQ: "quat",
  hingeAxis: "vec3",
  euler: "euler",
});

function computeLimitProximity(bone: THREE.Bone, objectId: string): number {
  const constraint = getConstraint(bone.name);
  if (!constraint) return 0;

  const restPose = getRestPose(objectId, bone.name);
  if (!restPose) return 0;

  lp.restQ.set(restPose[0], restPose[1], restPose[2], restPose[3]);
  lp.deltaQ.copy(lp.restQ).invert().multiply(bone.quaternion).normalize();

  let maxProximity = 0;

  const checkDimension = (fraction: number) => {
    // fraction: 0 = at rest, 1 = at limit
    if (fraction > 0.3 && fraction > 0.85) {
      const p = (fraction - 0.85) / 0.15;
      if (p > maxProximity) maxProximity = p;
    }
  };

  if (constraint.type === "hinge") {
    lp.hingeAxis.set(
      constraint.axis === "x" ? 1 : 0,
      constraint.axis === "y" ? 1 : 0,
      constraint.axis === "z" ? 1 : 0,
    );
    const angle = extractAxisAngle(lp.deltaQ, lp.hingeAxis);
    // Check proximity to max
    if (angle > 0 && constraint.maxAngle > 0) {
      checkDimension(angle / constraint.maxAngle);
    }
    // Check proximity to min
    if (angle < 0 && constraint.minAngle < 0) {
      checkDimension(angle / constraint.minAngle);
    }
  } else if (constraint.type === "per_axis") {
    const euler = lp.euler.setFromQuaternion(lp.deltaQ, "ZYX");
    if (constraint.xMax > 0) checkDimension(euler.x / constraint.xMax);
    if (constraint.xMin < 0) checkDimension(euler.x / constraint.xMin);
    if (constraint.yMax > 0) checkDimension(euler.y / constraint.yMax);
    if (constraint.yMin < 0) checkDimension(euler.y / constraint.yMin);
    if (constraint.zMax > 0) checkDimension(euler.z / constraint.zMax);
    if (constraint.zMin < 0) checkDimension(euler.z / constraint.zMin);
  }

  return Math.min(1, Math.max(0, maxProximity));
}

// ---------------------------------------------------------------------------
// Bone debug data pump — writes per-frame bone info to a global.
// Only mounted when debug is enabled AND a bone is active.
// ---------------------------------------------------------------------------

export interface BoneDebugSnapshot {
  boneName: string;
  parentName: string;
  localDeg: [string, string, string];
  worldDeg: [string, string, string];
  quat: [string, string, string, string];
  hasStoredPose: boolean;
  restDeg: [string, string, string] | null;
  twist: { bone: string; parent: string; delta: string } | null;
  cone: string | null;
  saturation: string | null;
  symmetry: {
    mirror: string;
    deltaXYZ: [string, string, string];
    total: string;
  } | null;
  localX: [string, string, string];
  localY: [string, string, string];
  localZ: [string, string, string];
}

const rot = makeScratch({
  worldQ: "quat",
  euler: "euler",
  axisX: "vec3",
  axisY: "vec3",
  axisZ: "vec3",
  restQ: "quat",
  deltaQ: "quat",
  parentDeltaQ: "quat",
  parentRestQ: "quat",
  mirrorDeltaQ: "quat",
  mirrorRestQ: "quat",
  swingQ: "quat",
  twistQ2: "quat",
});
const _unitX = new THREE.Vector3(1, 0, 0);
const _unitY = new THREE.Vector3(0, 1, 0);
const _unitZ = new THREE.Vector3(0, 0, 1);

/** Swap L↔R side suffix to get the mirror bone name, or null for center bones. */
function getMirrorBoneName(name: string): string | null {
  // Bones end with L/R optionally followed by digits (fingers): upper_armL, finger_indexR2
  const m = name.match(/^(.+?)(L|R)(\d*)$/);
  if (!m) return null;
  return m[1] + (m[2] === "L" ? "R" : "L") + m[3];
}

export function BoneDebugDataPump({
  bone,
  objectId,
}: {
  bone: THREE.Bone;
  objectId: string;
}) {
  const bonePoses = useStore((s) => s.mannequinBonePoses.get(objectId));

  useFrame(() => {
    const localQ = bone.quaternion;

    rot.euler.setFromQuaternion(localQ, "XYZ");
    const localDeg: [string, string, string] = [
      ((rot.euler.x * 180) / Math.PI).toFixed(1),
      ((rot.euler.y * 180) / Math.PI).toFixed(1),
      ((rot.euler.z * 180) / Math.PI).toFixed(1),
    ];

    bone.getWorldQuaternion(rot.worldQ);
    rot.euler.setFromQuaternion(rot.worldQ, "XYZ");
    const worldDeg: [string, string, string] = [
      ((rot.euler.x * 180) / Math.PI).toFixed(1),
      ((rot.euler.y * 180) / Math.PI).toFixed(1),
      ((rot.euler.z * 180) / Math.PI).toFixed(1),
    ];

    rot.axisX.set(1, 0, 0).applyQuaternion(rot.worldQ);
    rot.axisY.set(0, 1, 0).applyQuaternion(rot.worldQ);
    rot.axisZ.set(0, 0, 1).applyQuaternion(rot.worldQ);

    // Rotation from rest: delta = inv(rest) * current
    const RAD2DEG = 180 / Math.PI;
    let restDeg: [string, string, string] | null = null;
    let twist: { bone: string; parent: string; delta: string } | null = null;
    let cone: string | null = null;
    let saturation: string | null = null;
    let symmetry: {
      mirror: string;
      deltaXYZ: [string, string, string];
      total: string;
    } | null = null;

    const rest = getRestPose(objectId, bone.name);
    if (rest) {
      rot.restQ.set(rest[0], rest[1], rest[2], rest[3]);
      rot.deltaQ.copy(rot.restQ).invert().multiply(localQ);
      rot.deltaQ.normalize();

      // --- FromRest (per-axis) ---
      restDeg = [
        (extractAxisAngle(rot.deltaQ, _unitX) * RAD2DEG).toFixed(1),
        (extractAxisAngle(rot.deltaQ, _unitY) * RAD2DEG).toFixed(1),
        (extractAxisAngle(rot.deltaQ, _unitZ) * RAD2DEG).toFixed(1),
      ];

      // --- Twist factor: Y-axis twist for bone vs parent ---
      const boneTwist = extractAxisAngle(rot.deltaQ, _unitY) * RAD2DEG;
      let parentTwist = 0;
      const parent = bone.parent;
      if (parent && parent instanceof THREE.Bone) {
        const parentRest = getRestPose(objectId, parent.name);
        if (parentRest) {
          rot.parentRestQ.set(
            parentRest[0],
            parentRest[1],
            parentRest[2],
            parentRest[3],
          );
          rot.parentDeltaQ
            .copy(rot.parentRestQ)
            .invert()
            .multiply(parent.quaternion);
          rot.parentDeltaQ.normalize();
          parentTwist = extractAxisAngle(rot.parentDeltaQ, _unitY) * RAD2DEG;
        }
      }
      twist = {
        bone: boneTwist.toFixed(1),
        parent: parentTwist.toFixed(1),
        delta: Math.abs(boneTwist - parentTwist).toFixed(1),
      };

      // --- Cone angle (swing magnitude from rest) ---
      decomposeSwingTwist(rot.deltaQ, _unitY, rot.swingQ, rot.twistQ2);
      const swingAngle = 2 * Math.acos(Math.min(1, Math.abs(rot.swingQ.w)));
      cone = (swingAngle * RAD2DEG).toFixed(1);

      // --- Constraint saturation ---
      const constraint = getConstraint(bone.name);
      if (constraint) {
        if (constraint.type === "per_axis") {
          const e = new THREE.Euler().setFromQuaternion(rot.deltaQ, "ZYX");
          const xPct = Math.round(
            Math.max(
              constraint.xMax > 0 ? (e.x / constraint.xMax) * 100 : 0,
              constraint.xMin < 0 ? (e.x / constraint.xMin) * 100 : 0,
            ),
          );
          const yPct = Math.round(
            Math.max(
              constraint.yMax > 0 ? (e.y / constraint.yMax) * 100 : 0,
              constraint.yMin < 0 ? (e.y / constraint.yMin) * 100 : 0,
            ),
          );
          const zPct = Math.round(
            Math.max(
              constraint.zMax > 0 ? (e.z / constraint.zMax) * 100 : 0,
              constraint.zMin < 0 ? (e.z / constraint.zMin) * 100 : 0,
            ),
          );
          saturation = `x:${xPct}% y:${yPct}% z:${zPct}%`;
        } else {
          // Hinge: saturation within [minAngle, maxAngle]
          const hingeAxis = new THREE.Vector3(
            constraint.axis === "x" ? 1 : 0,
            constraint.axis === "y" ? 1 : 0,
            constraint.axis === "z" ? 1 : 0,
          );
          const hingeAngle = extractAxisAngle(rot.deltaQ, hingeAxis);
          const range = constraint.maxAngle - constraint.minAngle;
          const pos = hingeAngle - constraint.minAngle;
          const pct = Math.round((pos / range) * 100);
          saturation = `hinge:${pct}%`;
        }
      }

      // --- Symmetry delta (L↔R) ---
      const mirrorName = getMirrorBoneName(bone.name);
      if (mirrorName) {
        const mirrorRest = getRestPose(objectId, mirrorName);
        // Walk up to skeleton root to find mirror bone
        let root: THREE.Object3D = bone;
        while (root.parent && root.parent instanceof THREE.Bone)
          root = root.parent;
        if (root.parent) root = root.parent; // include the non-bone parent (skeleton)
        const mirrorBone = root.getObjectByName(mirrorName);
        if (mirrorRest && mirrorBone) {
          rot.mirrorRestQ.set(
            mirrorRest[0],
            mirrorRest[1],
            mirrorRest[2],
            mirrorRest[3],
          );
          rot.mirrorDeltaQ
            .copy(rot.mirrorRestQ)
            .invert()
            .multiply(mirrorBone.quaternion);
          rot.mirrorDeltaQ.normalize();
          const mx = extractAxisAngle(rot.mirrorDeltaQ, _unitX) * RAD2DEG;
          const my = extractAxisAngle(rot.mirrorDeltaQ, _unitY) * RAD2DEG;
          const mz = extractAxisAngle(rot.mirrorDeltaQ, _unitZ) * RAD2DEG;
          const bx = extractAxisAngle(rot.deltaQ, _unitX) * RAD2DEG;
          const by = extractAxisAngle(rot.deltaQ, _unitY) * RAD2DEG;
          const bz = extractAxisAngle(rot.deltaQ, _unitZ) * RAD2DEG;
          // Total angular difference between the two delta quaternions
          const dot = Math.min(
            1,
            Math.abs(
              rot.deltaQ.x * rot.mirrorDeltaQ.x +
                rot.deltaQ.y * rot.mirrorDeltaQ.y +
                rot.deltaQ.z * rot.mirrorDeltaQ.z +
                rot.deltaQ.w * rot.mirrorDeltaQ.w,
            ),
          );
          const totalDeg = 2 * Math.acos(dot) * RAD2DEG;
          symmetry = {
            mirror: mirrorName,
            deltaXYZ: [
              Math.abs(bx - mx).toFixed(1),
              Math.abs(by - my).toFixed(1),
              Math.abs(bz - mz).toFixed(1),
            ],
            total: totalDeg.toFixed(1),
          };
        }
      }
    }

    window.__poseref_boneDebug = {
      boneName: bone.name,
      parentName: bone.parent?.name ?? "(none)",
      localDeg,
      worldDeg,
      quat: [
        localQ.x.toFixed(3),
        localQ.y.toFixed(3),
        localQ.z.toFixed(3),
        localQ.w.toFixed(3),
      ],
      hasStoredPose: bonePoses?.get(bone.name) != null,
      restDeg,
      twist,
      cone,
      saturation,
      symmetry,
      localX: [
        rot.axisX.x.toFixed(2),
        rot.axisX.y.toFixed(2),
        rot.axisX.z.toFixed(2),
      ],
      localY: [
        rot.axisY.x.toFixed(2),
        rot.axisY.y.toFixed(2),
        rot.axisY.z.toFixed(2),
      ],
      localZ: [
        rot.axisZ.x.toFixed(2),
        rot.axisZ.y.toFixed(2),
        rot.axisZ.z.toFixed(2),
      ],
    };
  });

  // Clear on unmount so the DOM panel hides
  useEffect(() => {
    return () => {
      window.__poseref_boneDebug = null;
    };
  }, []);

  return null;
}
